import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BookOpen, Clock, ChefHat, Sparkles, Search, Filter } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mock data for meal plans
const mockMealPlans = [
  {
    plan_id: 1,
    name: "7-Day Diabetic Plan",
    description: "A balanced plan to help control blood sugar levels",
    duration: "7 days",
    total_recipes: 21,
    avg_carbs_per_day: 45
  },
  {
    plan_id: 2,
    name: "Mediterranean Plan",
    description: "Mediterranean diet adapted for diabetics",
    duration: "14 days",
    total_recipes: 42,
    avg_carbs_per_day: 50
  },
  {
    plan_id: 3,
    name: "Vegetarian Plan",
    description: "Healthy and balanced vegetarian options",
    duration: "10 days",
    total_recipes: 30,
    avg_carbs_per_day: 40
  }
];

const Recipes = () => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const recipesPerPage = 12;

  useEffect(() => {
    fetchRecipes();
  }, []);

  useEffect(() => {
    filterRecipes();
  }, [recipes, searchQuery, filterType]);

  const fetchRecipes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/recipes?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(response.data || []);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRecipes = () => {
    let filtered = recipes;

    // Filter by meal type
    if (filterType !== 'All') {
      filtered = filtered.filter(r => r.meal_type === filterType);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(r => 
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredRecipes(filtered);
    setCurrentPage(1);
  };

  // Pagination
  const indexOfLastRecipe = currentPage * recipesPerPage;
  const indexOfFirstRecipe = indexOfLastRecipe - recipesPerPage;
  const currentRecipes = filteredRecipes.slice(indexOfFirstRecipe, indexOfLastRecipe);
  const totalPages = Math.ceil(filteredRecipes.length / recipesPerPage);

  const difficultyColors = {
    Easy: 'bg-emerald-100 text-emerald-700',
    Medium: 'bg-blue-100 text-blue-700',
    Hard: 'bg-orange-100 text-orange-700'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <ChefHat className="w-7 h-7" />
          </div>
          <h2 className="text-3xl font-bold">Recipes & Meal Plans</h2>
        </div>
        <p className="text-orange-50 text-lg">Delicious, diabetes-friendly meals curated for you</p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="recipes" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="recipes">Recipes</TabsTrigger>
          <TabsTrigger value="meal-plans">Meal Plans</TabsTrigger>
        </TabsList>

        {/* Recipes Tab */}
        <TabsContent value="recipes" className="space-y-6">
          {/* Search and Filter */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search recipes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-gray-600" />
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="All">All meals</option>
                    <option value="Breakfast">Breakfast</option>
                    <option value="Lunch">Lunch</option>
                    <option value="Dinner">Dinner</option>
                    <option value="Snack">Snack</option>
                    <option value="Smoothie">Smoothie</option>
                  </select>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                {filteredRecipes.length} recipe{filteredRecipes.length !== 1 ? 's' : ''} found
              </p>
            </CardContent>
          </Card>

          {/* Generate Recipe Card */}
          <Card className="shadow-md border-0 bg-gradient-to-br from-purple-50 to-pink-50">
            <CardContent className="py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Food Scanner</h3>
                    <p className="text-sm text-gray-600">Scan barcodes and analyze carbs</p>
                  </div>
                </div>
                <Button 
                  onClick={() => window.location.href = '/food-scanner'}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6"
                >
                  Open Scanner
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recipe Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading recipes...</p>
            </div>
          ) : currentRecipes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-600">No recipes found</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentRecipes.map((recipe) => (
              <Card
                key={recipe.recipe_id}
                className="shadow-md border-0 hover:shadow-xl transition-all overflow-hidden group cursor-pointer"
                onClick={() => setSelectedRecipe(recipe)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={recipe.image}
                    alt={recipe.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className={difficultyColors[recipe.difficulty]}>
                      {recipe.difficulty}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{recipe.name}</CardTitle>
                  <CardDescription className="line-clamp-2">{recipe.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{recipe.prep_time}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <BookOpen className="w-4 h-4" />
                        <span>{recipe.ingredients.length} items</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Carbs</p>
                        <p className="font-semibold text-blue-700">{recipe.carbs}g</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Protein</p>
                        <p className="font-semibold text-emerald-700">{recipe.protein}g</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-600">Calories</p>
                        <p className="font-semibold text-orange-700">{recipe.calories}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center space-x-2 mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  
                  <div className="flex space-x-1">
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNum = index + 1;
                      // Show first, last, current, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? 'default' : 'outline'}
                            onClick={() => setCurrentPage(pageNum)}
                            className={currentPage === pageNum ? 'bg-orange-600' : ''}
                          >
                            {pageNum}
                          </Button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return <span key={pageNum} className="px-2">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Meal Plans Tab */}
        <TabsContent value="meal-plans" className="space-y-6">
          <Card className="shadow-md border-0 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">AI Meal Plan Generator</h3>
                    <p className="text-sm text-gray-600">Create a customized meal plan tailored to your needs</p>
                  </div>
                </div>
                <Button className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 shadow-lg hover:shadow-xl transition-all">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Plan
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockMealPlans.map((plan) => (
              <Card
                key={plan.plan_id}
                className="shadow-md border-0 hover:shadow-xl transition-all"
              >
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-emerald-100 text-emerald-700">{plan.duration}</Badge>
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Total Recipes</span>
                      <span className="font-semibold text-gray-900">{plan.total_recipes}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Avg. Carbs/Day</span>
                      <span className="font-semibold text-blue-700">{plan.avg_carbs_per_day}g</span>
                    </div>
                    <Button className="w-full mt-4 bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600">
                      View Plan
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Recipes;
